// T5-TASK-SEARCH: Search tasks (`/work/search?q=`). The same filters a DE has in `search_tasks`
// — text, boards, statuses, assignees, tags, a due-date range and field conditions (number and
// date ranges, choices, text, yes/no) — over every task the person may open (the server's task
// read rules; nothing is filtered here). Each hit shows its brief (the description's first
// lines), its latest numbers, how many tasks it links to, and its last status change with who
// made it. Filters are menus and one modal per field condition; active ones are chips.
import type { OperationOutput } from "@companyos/sdk";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DueLabel,
  EmptyState,
  Field,
  FieldLabel,
  FilterChip,
  FilterChips,
  InfoTip,
  Input,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  SearchField,
  Skeleton,
  Toolbar,
  ToolbarButton,
} from "@companyos/ui";
import { useInfiniteQuery, useQueries, useQuery } from "@tanstack/react-query";
import {
  CalendarRangeIcon,
  CircleDotIcon,
  LinkIcon,
  ListFilterIcon,
  SearchIcon,
  SlidersHorizontalIcon,
  SquareKanbanIcon,
  TagIcon,
  UserIcon,
} from "lucide-react";
import { useEffect, useId, useMemo, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { formatRecent } from "../format";
import { LoadError } from "../screen-parts";
import { type SeatPeople, useSeatPeople } from "./model";
import { tagsQuery } from "./tags";
import {
  condKindOf,
  dueChip,
  EMPTY_SEARCH,
  type FieldCond,
  fieldChip,
  hasFilters,
  type SearchState,
  searchBody,
} from "./task-search-model";
import type { Board, FieldDef } from "./view-engine";
import { boardsQuery, fieldsQuery, workKeys } from "./work-queries";
import { BODY } from "./work-screens";

type Hit = OperationOutput<"tasks.search">["data"][number];
const PAGE = 50;

/** Debounces a value (the text box searches as the person types). */
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function TaskSearchScreen({
  api,
  company,
  initialQuery,
  onQuery,
  onOpenTask,
}: {
  api: Api;
  company: CompanyAccess;
  initialQuery: string;
  onQuery: (q: string) => void;
  onOpenTask: (id: string) => void;
}) {
  const co = company.id;
  const [s, setS] = useState<SearchState>({ ...EMPTY_SEARCH, q: initialQuery });
  const set = (patch: Partial<SearchState>) => setS((prev) => ({ ...prev, ...patch }));
  const debouncedQ = useDebounced(s.q, 250);
  const state = { ...s, q: debouncedQ };
  const body = searchBody(state, PAGE);
  const active = hasFilters(s) || debouncedQ.trim().length > 0;
  const people = useSeatPeople(api, co);
  const boards = useQuery(boardsQuery(api, co));
  const tags = useQuery(tagsQuery(api, co));
  const liveBoards = (boards.data?.data ?? []).filter((b) => b.archived_at === null);
  const boardOf = (id: string) => liveBoards.find((b) => b.id === id);
  const results = useInfiniteQuery({
    queryKey: [...workKeys.all(co), "task-search", body] as const,
    enabled: active,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.tasks.search({
        params: { co },
        body: { ...body, ...(pageParam ? { cursor: pageParam } : {}) },
      }),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
  useEffect(() => onQuery(debouncedQ.trim()), [debouncedQ, onQuery]);
  // A new `?q=` from outside (a link, back/forward) replaces the words in the box.
  useEffect(() => {
    setS((prev) => (prev.q.trim() === initialQuery ? prev : { ...prev, q: initialQuery }));
  }, [initialQuery]);
  const hits = results.data?.pages.flatMap((p) => p.data) ?? [];
  // Statuses on offer: the picked boards' (every board's when none is picked), one per key.
  const statusBoards = s.boards.length
    ? liveBoards.filter((b) => s.boards.includes(b.id))
    : liveBoards;
  const statuses = useMemo(() => {
    const seen = new Map<string, string>();
    for (const b of statusBoards)
      for (const st of b.status_set) if (!seen.has(st.key)) seen.set(st.key, st.name);
    return [...seen].map(([key, name]) => ({ key, name }));
  }, [statusBoards]);
  const [fieldOpen, setFieldOpen] = useState(false);
  const [dueOpen, setDueOpen] = useState(false);
  // The route takes at most 50 boards; a 51st tick is ignored.
  const toggle = (k: "boards" | "statuses" | "assignees" | "tags", v: string, on: boolean) => {
    if (on && k === "boards" && s.boards.length >= 50) return;
    set({ [k]: on ? [...s[k], v] : s[k].filter((x) => x !== v) } as Partial<SearchState>);
  };
  const tagName = (id: string) => tags.data?.find((t) => t.id === id)?.name ?? id;
  const statusName = (key: string) => statuses.find((x) => x.key === key)?.name ?? key;

  return (
    <div data-screen="task-search" className="flex min-h-full min-w-0 flex-col">
      <LocationHeader
        icon={SearchIcon}
        title="Search tasks"
        badge={
          <InfoTip label="About task search">
            Every task you can open, by text, board, status, people, tags, dates and field values.
            DEs search with the same filters.
          </InfoTip>
        }
      />
      <div className={BODY}>
        <Toolbar aria-label="Task search tools">
          <SearchField
            value={s.q}
            placeholder="Search tasks…"
            aria-label="Search tasks"
            autoFocus
            onChange={(e) => set({ q: e.target.value })}
          />
          <PickMenu
            icon={SquareKanbanIcon}
            label="Board"
            count={s.boards.length}
            items={liveBoards.map((b) => ({ id: b.id, name: b.name }))}
            picked={s.boards}
            onToggle={(id, on) => toggle("boards", id, on)}
          />
          <PickMenu
            icon={CircleDotIcon}
            label="Status"
            count={s.statuses.length}
            items={statuses.map((x) => ({ id: x.key, name: x.name }))}
            picked={s.statuses}
            onToggle={(id, on) => toggle("statuses", id, on)}
            extra={
              <>
                <DropdownMenuSeparator />
                <DropdownMenuCheckboxItem
                  checked={s.includeDone}
                  onCheckedChange={(on) => set({ includeDone: Boolean(on) })}
                >
                  Show done tasks
                </DropdownMenuCheckboxItem>
              </>
            }
          />
          <PickMenu
            icon={UserIcon}
            label="Assignee"
            count={s.assignees.length}
            items={people.all
              .filter((p) => !p.closed)
              .map((p) => ({ id: p.seatId, name: p.name, note: p.de ? "DE" : undefined }))}
            picked={s.assignees}
            onToggle={(id, on) => toggle("assignees", id, on)}
          />
          <PickMenu
            icon={TagIcon}
            label="Tag"
            count={s.tags.length}
            items={(tags.data ?? []).map((t) => ({ id: t.id, name: t.name }))}
            picked={s.tags}
            onToggle={(id, on) => toggle("tags", id, on)}
          />
          <ToolbarButton
            icon={CalendarRangeIcon}
            label="Due"
            active={Boolean(s.due.from || s.due.to)}
            onClick={() => setDueOpen(true)}
          />
          <ToolbarButton
            icon={SlidersHorizontalIcon}
            label="Field"
            active={s.fields.length > 0}
            {...(s.fields.length ? { count: s.fields.length } : {})}
            onClick={() => setFieldOpen(true)}
          />
          <span className="shrink-0 text-xs text-muted-foreground tabular-nums" aria-live="polite">
            {active && results.data ? `${hits.length}${results.hasNextPage ? "+" : ""} found` : ""}
          </span>
        </Toolbar>
        <ActiveChips
          s={s}
          set={set}
          boardName={(id) => boardOf(id)?.name ?? "Board"}
          statusName={statusName}
          people={people}
          tagName={tagName}
        />
        {!active ? (
          <EmptyState
            variant="compact"
            icon={SearchIcon}
            title="Search every task"
            description="Type, or pick a filter."
          />
        ) : results.isPending ? (
          <div role="status" aria-label="Searching" className="mt-2 flex flex-col gap-2">
            <Skeleton className="h-14" />
            <Skeleton className="h-14" />
            <Skeleton className="h-14" />
          </div>
        ) : results.isError ? (
          <LoadError what="The search" error={results.error} />
        ) : hits.length === 0 ? (
          <EmptyState
            variant="compact"
            icon={ListFilterIcon}
            title="No task matches"
            description="Loosen a filter or change the words."
          />
        ) : (
          <ul aria-label="Search results" className="mt-1 flex flex-col divide-y divide-border">
            {hits.map((t) => (
              <HitRow
                key={t.id}
                hit={t}
                board={boardOf(t.board_id)}
                people={people}
                onOpen={() => onOpenTask(t.id)}
              />
            ))}
          </ul>
        )}
        {results.hasNextPage ? (
          <div className="py-3">
            <Button
              variant="outline"
              size="sm"
              disabled={results.isFetchingNextPage}
              onClick={() => void results.fetchNextPage()}
            >
              Show more
            </Button>
          </div>
        ) : null}
      </div>
      <DueDialog
        key={`due-${String(dueOpen)}`}
        open={dueOpen}
        onOpenChange={setDueOpen}
        due={s.due}
        onSave={(due) => set({ due })}
      />
      <FieldDialog
        key={`field-${String(fieldOpen)}`}
        api={api}
        co={co}
        open={fieldOpen}
        onOpenChange={setFieldOpen}
        boards={s.boards.length ? liveBoards.filter((b) => s.boards.includes(b.id)) : liveBoards}
        onAdd={(c) => set({ fields: [...s.fields.filter((x) => x.fieldId !== c.fieldId), c] })}
      />
    </div>
  );
}

function PickMenu({
  icon,
  label,
  count,
  items,
  picked,
  onToggle,
  extra,
}: {
  icon: typeof SearchIcon;
  label: string;
  count: number;
  items: { id: string; name: string; note?: string | undefined }[];
  picked: string[];
  onToggle: (id: string, on: boolean) => void;
  extra?: React.ReactNode;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <ToolbarButton
            icon={icon}
            label={label}
            active={count > 0}
            {...(count ? { count } : {})}
          />
        }
      />
      <DropdownMenuContent align="start" className="max-h-[60vh] min-w-56 overflow-y-auto">
        <DropdownMenuGroup>
          <DropdownMenuLabel>{label}</DropdownMenuLabel>
          {items.length === 0 ? (
            <DropdownMenuItem disabled>None yet</DropdownMenuItem>
          ) : (
            items.map((it) => (
              <DropdownMenuCheckboxItem
                key={it.id}
                checked={picked.includes(it.id)}
                onCheckedChange={(on) => onToggle(it.id, Boolean(on))}
              >
                <span className="min-w-0 flex-1 truncate">{it.name}</span>
                {it.note ? (
                  <span className="ml-auto text-xs text-muted-foreground">{it.note}</span>
                ) : null}
              </DropdownMenuCheckboxItem>
            ))
          )}
        </DropdownMenuGroup>
        {extra}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ActiveChips({
  s,
  set,
  boardName,
  statusName,
  people,
  tagName,
}: {
  s: SearchState;
  set: (p: Partial<SearchState>) => void;
  boardName: (id: string) => string;
  statusName: (key: string) => string;
  people: SeatPeople;
  tagName: (id: string) => string;
}) {
  if (!hasFilters(s)) return null;
  const due = dueChip(s.due);
  return (
    <FilterChips aria-label="Active filters" className="mt-1">
      {s.boards.length ? (
        <FilterChip
          label={`Board: ${s.boards.map(boardName).join(", ")}`}
          removeLabel="Remove the board filter"
          onRemove={() => set({ boards: [] })}
        />
      ) : null}
      {s.statuses.length ? (
        <FilterChip
          label={`Status: ${s.statuses.map(statusName).join(", ")}`}
          removeLabel="Remove the status filter"
          onRemove={() => set({ statuses: [] })}
        />
      ) : null}
      {!s.includeDone ? (
        <FilterChip
          label="Hiding done tasks"
          removeLabel="Show done tasks"
          onRemove={() => set({ includeDone: true })}
        />
      ) : null}
      {s.assignees.length ? (
        <FilterChip
          label={`Assignee: ${s.assignees.map((id) => people.of(id).label).join(", ")}`}
          removeLabel="Remove the assignee filter"
          onRemove={() => set({ assignees: [] })}
        />
      ) : null}
      {s.tags.length ? (
        <FilterChip
          label={`Tag: ${s.tags.map(tagName).join(", ")}`}
          removeLabel="Remove the tag filter"
          onRemove={() => set({ tags: [] })}
        />
      ) : null}
      {due ? (
        <FilterChip
          label={due}
          removeLabel="Remove the due filter"
          onRemove={() => set({ due: {} })}
        />
      ) : null}
      {s.fields.map((c) => (
        <FilterChip
          key={c.fieldId}
          label={fieldChip(c)}
          removeLabel={`Remove the ${c.label} filter`}
          onRemove={() => set({ fields: s.fields.filter((x) => x.fieldId !== c.fieldId) })}
        />
      ))}
    </FilterChips>
  );
}

function HitRow({
  hit: t,
  board,
  people,
  onOpen,
}: {
  hit: Hit;
  board: Board | undefined;
  people: SeatPeople;
  onOpen: () => void;
}) {
  const status = (key: string) => board?.status_set.find((x) => x.key === key)?.name ?? key;
  const ch = t.last_status_change ?? null;
  const who =
    ch && "membership_id" in ch.actor && ch.actor.membership_id
      ? people.nameOf(ch.actor.membership_id).name
      : ch?.actor.kind === "system"
        ? "System"
        : null;
  const links = t.links ?? [];
  return (
    <li>
      <button
        type="button"
        data-hit={t.id}
        onClick={onOpen}
        className="flex w-full min-w-0 flex-col gap-1 rounded-sm px-2 py-2.5 text-left hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring"
      >
        <span className="flex min-w-0 items-baseline gap-2">
          <span className="shrink-0 text-xs text-muted-foreground tabular-nums">#{t.number}</span>
          <span className="min-w-0 flex-1 truncate text-sm font-medium">{t.title}</span>
          <span className="shrink-0 text-xs text-muted-foreground">
            {board?.name ?? ""}
            {board ? " · " : ""}
            {status(t.status)}
          </span>
          {t.due_at ? (
            <span className="shrink-0 text-xs text-muted-foreground">
              <DueLabel due={t.due_at} />
            </span>
          ) : null}
        </span>
        {t.brief ? (
          <span className="line-clamp-2 whitespace-pre-line text-xs text-secondary">{t.brief}</span>
        ) : null}
        {t.numbers?.length || links.length || ch ? (
          <span className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            {(t.numbers ?? []).slice(0, 4).map((n) => (
              <span key={n.metric} className="tabular-nums">
                {n.metric} <span className="font-medium text-foreground">{n.value}</span>
                {n.unit ? ` ${n.unit}` : ""}
              </span>
            ))}
            {links.length ? (
              <span className="inline-flex items-center gap-1">
                <LinkIcon aria-hidden="true" className="size-3" />
                {links.length} linked
              </span>
            ) : null}
            {ch ? (
              <span>
                {ch.from === null ? "Moved to " : "→ "}
                {status(ch.to)}
                {who ? ` · ${who}` : ""} · {formatRecent(ch.at)}
              </span>
            ) : null}
          </span>
        ) : null}
      </button>
    </li>
  );
}

function DueDialog({
  open,
  onOpenChange,
  due,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  due: SearchState["due"];
  onSave: (due: SearchState["due"]) => void;
}) {
  const [from, setFrom] = useState(due.from ?? "");
  const [to, setTo] = useState(due.to ?? "");
  const id = useId();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            onSave({ ...(from ? { from } : {}), ...(to ? { to } : {}) });
            onOpenChange(false);
          }}
        >
          <DialogHeader>
            <DialogTitle>Due between</DialogTitle>
            <DialogDescription>Either end may stay empty.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field>
              <FieldLabel htmlFor={`${id}-from`}>From</FieldLabel>
              <Input
                id={`${id}-from`}
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor={`${id}-to`}>To</FieldLabel>
              <Input
                id={`${id}-to`}
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </Field>
          </div>
          <DialogFooter>
            <Button type="submit">Apply</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FieldDialog({
  api,
  co,
  open,
  onOpenChange,
  boards,
  onAdd,
}: {
  api: Api;
  co: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  boards: Board[];
  onAdd: (c: FieldCond) => void;
}) {
  // The fields of the boards searched (at most 20 boards: pick boards to narrow).
  const scope = boards.slice(0, 20);
  const lists = useQueries({
    queries: scope.map((b) => ({ ...fieldsQuery(api, co, b.id), enabled: open })),
  });
  const fields: (FieldDef & { boardName: string })[] = lists.flatMap((q, i) =>
    (q.data?.data ?? [])
      .filter((f) => condKindOf(f.type) !== null)
      .map((f) => ({ ...f, boardName: scope[i]?.name ?? "" })),
  );
  const loading = lists.some((q) => q.isPending);
  const [fieldId, setFieldId] = useState("");
  const [min, setMin] = useState("");
  const [max, setMax] = useState("");
  const [text, setText] = useState("");
  const [choices, setChoices] = useState<string[]>([]);
  const [yes, setYes] = useState(true);
  const id = useId();
  const f = fields.find((x) => x.id === fieldId);
  const kind = f ? condKindOf(f.type) : null;
  const options =
    (f?.options as { choices?: { key: string; name: string }[] } | null)?.choices ?? [];
  const ready =
    f !== undefined &&
    (kind === "number" || kind === "date"
      ? Boolean(min || max)
      : kind === "choice"
        ? choices.length > 0
        : kind === "text"
          ? text.trim().length > 0
          : true);
  const build = (): FieldCond | null => {
    if (!f || !kind) return null;
    const base = { fieldId: f.id, label: f.name };
    if (kind === "number" || kind === "date")
      return {
        ...base,
        kind: "range",
        date: kind === "date",
        ...(min ? { min } : {}),
        ...(max ? { max } : {}),
      };
    if (kind === "choice")
      return {
        ...base,
        kind: "in",
        values: choices,
        names: choices.map((k) => options.find((o) => o.key === k)?.name ?? k),
      };
    if (kind === "text") return { ...base, kind: "contains", text: text.trim() };
    return { ...base, kind: "bool", value: yes };
  };
  const multiBoard = new Set(fields.map((x) => x.boardName)).size > 1;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            const c = build();
            if (c) onAdd(c);
            onOpenChange(false);
          }}
        >
          <DialogHeader>
            <DialogTitle>Filter by a field</DialogTitle>
            <DialogDescription>
              {boards.length > scope.length
                ? `Fields of the first ${scope.length} boards: pick boards to see others.`
                : "Numbers and dates take a range."}
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel htmlFor={`${id}-field`}>Field</FieldLabel>
            <NativeSelect
              id={`${id}-field`}
              value={fieldId}
              onChange={(e) => {
                setFieldId(e.target.value);
                setMin("");
                setMax("");
                setText("");
                setChoices([]);
              }}
            >
              <NativeSelectOption value="">
                {loading
                  ? "Loading fields…"
                  : fields.length
                    ? "Pick a field"
                    : "No fields to filter"}
              </NativeSelectOption>
              {fields.map((x) => (
                <NativeSelectOption key={x.id} value={x.id}>
                  {multiBoard ? `${x.name} · ${x.boardName}` : x.name}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </Field>
          {kind === "number" || kind === "date" ? (
            <div className="grid grid-cols-2 gap-3">
              <Field>
                <FieldLabel htmlFor={`${id}-min`}>
                  {kind === "date" ? "From" : "At least"}
                </FieldLabel>
                <Input
                  id={`${id}-min`}
                  type={kind === "date" ? "date" : "number"}
                  {...(kind === "number" ? { step: "any" } : {})}
                  value={min}
                  onChange={(e) => setMin(e.target.value)}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor={`${id}-max`}>{kind === "date" ? "To" : "At most"}</FieldLabel>
                <Input
                  id={`${id}-max`}
                  type={kind === "date" ? "date" : "number"}
                  {...(kind === "number" ? { step: "any" } : {})}
                  value={max}
                  onChange={(e) => setMax(e.target.value)}
                />
              </Field>
            </div>
          ) : null}
          {kind === "choice" ? (
            <fieldset className="grid gap-1.5">
              <legend className="mb-1 text-sm font-medium">Any of</legend>
              {options.map((o) => (
                <label key={o.key} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={choices.includes(o.key)}
                    onChange={(e) =>
                      setChoices((prev) =>
                        e.target.checked ? [...prev, o.key] : prev.filter((k) => k !== o.key),
                      )
                    }
                  />
                  {o.name}
                </label>
              ))}
            </fieldset>
          ) : null}
          {kind === "text" ? (
            <Field>
              <FieldLabel htmlFor={`${id}-text`}>Contains</FieldLabel>
              <Input id={`${id}-text`} value={text} onChange={(e) => setText(e.target.value)} />
            </Field>
          ) : null}
          {kind === "bool" ? (
            <Field>
              <FieldLabel htmlFor={`${id}-bool`}>Is</FieldLabel>
              <NativeSelect
                id={`${id}-bool`}
                value={yes ? "yes" : "no"}
                onChange={(e) => setYes(e.target.value === "yes")}
              >
                <NativeSelectOption value="yes">Ticked</NativeSelectOption>
                <NativeSelectOption value="no">Not ticked</NativeSelectOption>
              </NativeSelect>
            </Field>
          ) : null}
          <DialogFooter>
            <Button type="submit" disabled={!ready}>
              Add filter
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
